import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

import { palette as p } from './palette';

// Design values antd has no token for. They are emitted as CSS variables
// next to antd's own (`appSidebarWidth` -> `var(--ant-app-sidebar-width)`),
// so CSS Modules read them the same way and they switch with the theme mode.
interface AppToken {
  // Weight of running text: medium, between antd's normal and strong
  // A string: numeric custom tokens are emitted with a px unit
  appFontWeightBody: string;
  // Section and dialog titles, between antd's heading 3 and 4
  appFontSizeTitle: number;
  // Display sizes above antd's heading scale
  appFontSizeDisplay: number;
  appFontSizeHero: number;
  appFontSizeGiant: number;
  // Height of the top bar of every page
  appHeaderHeight: number;
  // A deeper primary for filled marks that sit on the primary background
  appColorPrimaryStrong: string;
  // The dark parts of the logo: brand navy on light, light on dark
  appBrandInk: string;
  // The animated title cuts its letters out of a surface through a blend
  // mode: lighten on a light surface, darken on a dark one
  appCutoutBlendMode: string;
  appCutoutInk: string;
  // Colors that run behind the animated title
  appSparkle1: string;
  appSparkle2: string;
  appSparkle3: string;
  appSparkle4: string;
  appSparkle5: string;
  // Shadow under surfaces floating above the page: notifications, cards on hover
  appShadowFloating: string;
}

declare module 'antd/es/theme/interface/alias' {
  // Custom tokens are typed like antd's own ones
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AliasToken extends AppToken {}
}

type Tokens = NonNullable<ThemeConfig['token']>;
type Components = NonNullable<ThemeConfig['components']>;

const sharedTokens: Tokens = {
  colorPrimary: p.blue500,
  colorLink: p.blue500,
  colorSuccess: p.green500,
  colorWarning: p.yellow500,
  colorError: p.red500,
  colorInfo: p.blue500,
  colorTextLightSolid: p.white,

  fontFamily: 'Inter, sans-serif',
  fontSize: 14,
  borderRadius: 12,
  borderRadiusLG: 16,
  boxShadow: 'none',
  colorPrimaryHover: p.blue400,
  colorPrimaryActive: p.blue400,
  colorErrorHover: p.red400,

  appFontWeightBody: '500',
  appFontSizeTitle: 22,
  appFontSizeDisplay: 48,
  appFontSizeHero: 64,
  appFontSizeGiant: 80,
  appHeaderHeight: 72,
  appColorPrimaryStrong: p.blue600,
  appSparkle1: p.blue500,
  appSparkle2: p.purple500,
  appSparkle3: p.pink500,
  appSparkle4: p.yellow500,
  appSparkle5: p.green500,
  appShadowFloating: '0 8px 24px rgba(15, 39, 79, 0.16)',
};

const lightTokens: Tokens = {
  ...sharedTokens,
  appBrandInk: p.brandInk,
  appCutoutBlendMode: 'lighten',
  appCutoutInk: p.black,
  colorTextHeading: p.neutral800,
  colorText: p.neutral500,
  colorTextSecondary: p.neutral300,
  colorTextPlaceholder: p.neutral300,
  colorBgContainer: p.white,
  colorBgLayout: p.white,
  colorBgElevated: p.white,
  colorBgContainerDisabled: p.neutral100,
  colorTextDisabled: p.neutral300,
  colorBorder: p.neutral100,
  colorBorderSecondary: p.neutral100,
  colorSplit: p.neutral100,
  colorPrimaryBg: p.blue100,
  colorErrorBg: p.red100,
  colorSuccessBg: p.green100,
  colorWarningBg: p.yellow100,
  colorBgSpotlight: p.neutral800,
  colorBgMask: 'rgba(6, 61, 140, 0.3)',
  colorIcon: p.blue500,
  colorIconHover: p.blue400,
};

const darkTokens: Tokens = {
  ...sharedTokens,
  appBrandInk: p.neutral200,
  appCutoutBlendMode: 'darken',
  appCutoutInk: p.white,
  colorTextHeading: p.neutral200,
  colorText: p.neutral500,
  colorTextSecondary: p.neutral300,
  colorTextPlaceholder: p.neutral500,
  colorBgContainer: p.dark,
  colorBgLayout: p.dark,
  colorBgElevated: p.neutral900,
  colorBgContainerDisabled: p.blue800,
  colorTextDisabled: p.blue700,
  colorBorder: p.blue900,
  colorBorderSecondary: p.blue900,
  colorSplit: p.blue900,
  colorPrimaryBg: p.blue900,
  colorErrorBg: p.red900,
  colorSuccessBg: p.green900,
  colorWarningBg: p.yellow900,
  colorBgSpotlight: p.neutral900,
  colorBgMask: 'rgba(7, 47, 148, 0.3)',
  colorIcon: p.blue500,
  colorIconHover: p.blue400,
};

const components = (t: Tokens): Components => ({
  Avatar: {
    containerSizeLG: 48,
  },
  Button: {
    controlHeight: 48,
    fontWeight: 500,
    paddingInline: 24,
    primaryShadow: 'none',
    defaultShadow: 'none',
    dangerShadow: 'none',
    defaultColor: t.colorTextHeading,
    defaultBg: t.colorBgContainer,
    defaultBorderColor: t.colorBorder,
    defaultHoverColor: t.colorTextHeading,
    defaultHoverBg: t.colorBgContainer,
    defaultHoverBorderColor: t.colorText,
    defaultActiveColor: t.colorTextHeading,
    defaultActiveBg: t.colorBorder,
    defaultActiveBorderColor: t.colorText,
    borderColorDisabled: 'transparent',
  },
  Form: {
    itemMarginBottom: 16,
  },
  Input: {
    controlHeight: 48,
    controlHeightLG: 48,
    paddingInline: 15,
    // Password fields are `large`: same side padding, so icons line up
    paddingInlineLG: 15,
    activeShadow: 'none',
    errorActiveShadow: 'none',
    hoverBorderColor: t.colorPrimary,
  },
  DatePicker: {
    controlHeight: 48,
    activeShadow: 'none',
    // Dates and the typed value as dark as the value of the inputs
    colorText: t.colorTextHeading,
  },
  Modal: {
    contentBg: t.colorBgContainer,
    headerBg: 'transparent',
    footerBg: 'transparent',
    titleColor: t.colorTextHeading,
    titleFontSize: t.appFontSizeTitle,
    titleLineHeight: 32 / 22,
  },
  Layout: {
    bodyBg: t.colorBgLayout,
    headerBg: t.colorBgContainer,
    siderBg: t.colorBgContainer,
  },
  Menu: {
    collapsedWidth: 48,
    itemHeight: 48,
    itemBorderRadius: 12,
    itemMarginInline: 0,
    itemMarginBlock: 8,
    itemPaddingInline: 12,
    iconSize: 24,
    collapsedIconSize: 24,
    iconMarginInlineEnd: 12,
    itemBg: 'transparent',
    itemColor: t.colorText,
    itemHoverColor: t.colorPrimary,
    itemHoverBg: t.colorPrimaryBg,
    itemSelectedColor: t.colorPrimary,
    itemSelectedBg: t.colorPrimaryBg,
    itemActiveBg: t.colorPrimaryBg,
    activeBarBorderWidth: 0,
  },
  Notification: {
    width: 716,
    colorBgElevated: t.colorBgContainer,
  },
  Select: {
    controlHeight: 48,
    optionFontSize: 14,
    // The value is as dark as the text of the inputs next to it
    colorText: t.colorTextHeading,
    selectorBg: 'transparent',
    // No glow around a focused select, only the blue border
    activeOutlineColor: 'transparent',
    optionActiveBg: t.colorPrimaryBg,
    optionSelectedColor: p.white,
    optionSelectedBg: t.colorPrimary,
    controlItemBgActiveHover: t.colorPrimary,
  },
  Radio: {
    radioSize: 24,
    dotSize: 16,
  },
  Segmented: {
    trackBg: 'transparent',
    trackPadding: 0,
    itemColor: t.colorText,
    itemHoverColor: t.colorTextHeading,
    itemHoverBg: 'transparent',
    itemSelectedBg: t.colorPrimary,
    itemSelectedColor: p.white,
    controlHeight: 40,
    controlHeightLG: 48,
    borderRadius: 10,
    borderRadiusLG: 12,
  },
  Slider: {
    trackBg: t.colorPrimary,
    trackHoverBg: t.colorIconHover,
    handleColor: t.colorPrimary,
    handleActiveColor: t.colorIconHover,
    dotBorderColor: t.colorBgContainer,
    dotActiveBorderColor: t.colorBgContainer,
  },
  Table: {
    headerBg: 'transparent',
    headerColor: t.colorText,
    headerSplitColor: 'transparent',
    rowHoverBg: 'transparent',
    borderColor: t.colorBorder,
    colorBgContainer: 'transparent',
    // antd mixes these from colorBgContainer, which turns black when it is
    // transparent: the sorted column takes the light primary tint instead
    headerSortActiveBg: t.colorPrimaryBg,
    headerSortHoverBg: t.colorPrimaryBg,
    fixedHeaderSortActiveBg: t.colorPrimaryBg,
    bodySortBg: t.colorPrimaryBg,
    footerBg: 'transparent',
    filterDropdownBg: t.colorBgElevated,
  },
  Tag: {
    borderRadiusSM: 12,
  },
  Tooltip: {
    maxWidth: 206,
    sizePopupArrow: 11,
    borderRadius: 8,
    fontSize: 12,
  },
});

export const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: lightTokens,
  components: components(lightTokens),
};

export const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: darkTokens,
  components: components(darkTokens),
};
